import { useCallback, useMemo } from 'react';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { menuItems, SPECIAL_EMAIL_ACCESS, canViewPageAccess } from './menuConfig';

/**
 * Menu items the signed-in user may see, each with its `visibleSubmenu`.
 * Shared by the sidebar and the top bar's menu search so both list exactly the
 * same pages. RequireAuthorization applies the same rules per route.
 */
const useVisibleMenuItems = () => {
  const { userAuthorization, employeeData, isRegionManager, isCenterSmoBackup, pageAccess } = useUserProfile();
  const userEmail = employeeData?.eMail?.toLowerCase() || '';

  // Pure role check (no path overrides). Empty/undefined means "everyone".
  const hasRole = useCallback((requiredAuth) => {
    if (!requiredAuth || requiredAuth.length === 0) return true;
    return requiredAuth.some(auth => userAuthorization.includes(auth));
  }, [userAuthorization]);

  // Precise per-path grant: hardcoded email allow-list OR admin-managed DB
  // pageAccess. Used to grant access to a specific page beyond the user's role.
  const hasPathGrant = useCallback((path) => {
    // Sayfa Yetkileri ekranı yalnızca sabit listedeki kişilere açılır;
    // DB üzerinden verilen pageAccess bu sayfayı asla açmaz.
    if (path === '/sayfa-yetkileri') return canViewPageAccess(userEmail);
    const specialPaths = SPECIAL_EMAIL_ACCESS[userEmail];
    if (specialPaths && specialPaths.includes(path)) return true;
    if (Array.isArray(pageAccess) && pageAccess.includes(path)) return true;
    return false;
  }, [userEmail, pageAccess]);

  // Combined check used for top-level menu items: role OR explicit path grant.
  const hasRequiredAuth = useCallback((requiredAuth, path) => {
    if (hasPathGrant(path)) return true;
    return hasRole(requiredAuth);
  }, [hasPathGrant, hasRole]);

  // "Bölge Müdürleri Raporları" is restricted to admins and employees who are
  // actually assigned as bölge müdürü to a region (dynamic, not a static role).
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');

  return useMemo(() => {
    return menuItems.map(item => {
      const parentAllowed = hasRequiredAuth(item.requiredAuth, item.path);

      const visibleSubmenu = item.submenu.filter(sub => {
        // Precise grant for this exact page (email allow-list or DB pageAccess).
        const granted = hasPathGrant(sub.path);
        // Region reports: admins, assigned region managers, or an explicit grant.
        if (sub.path === '/bolge-muduru-reports') {
          return isAdmin || isRegionManager || granted;
        }
        // Role inheritance: a submenu is visible if the user's role satisfies
        // the submenu's own requiredAuth (or, when it has none, the parent's).
        const roleOk = hasRole(sub.requiredAuth || item.requiredAuth);
        // SMO Lead reports: also visible to a center's backup (Yedek SMO).
        if (sub.path === '/smo-lead-reports') {
          return granted || roleOk || isCenterSmoBackup;
        }
        return granted || roleOk;
      });

      // Show the parent if the user can access it normally, or if it has at
      // least one visible submenu item (e.g. a user granted only one report).
      const itemVisible = parentAllowed || visibleSubmenu.length > 0;
      return itemVisible ? { ...item, visibleSubmenu } : null;
    }).filter(Boolean);
  }, [hasRequiredAuth, hasPathGrant, hasRole, isAdmin, isRegionManager, isCenterSmoBackup]);
};

export default useVisibleMenuItems;
